import React, { useContext, useEffect } from 'react'
import { Navigate } from 'react-router-dom'
import { useShallow } from 'zustand/react/shallow'

import { isAuthenticationRejected, useLogin } from '@api/login/state'

import { ILoginPage } from './types'
import { LoginPage } from './ui'
import { useTheme } from '@hooks/useTheme'
import { useValue } from '@hooks/useValue'
import { reportUnexpectedClientError } from '@services/client-error.service'
import { NotificationContext, notifyOpen } from '@ui/notification/notification.provider'
import { Loader } from '@ui/index'


const SESSION_RESTORE_RETRY_MS = 5000


/** Контейнер страницы - Авторизация */
export const LoginContainer = () => {
    const { theme, toggleTheme } = useTheme()
    const notify = useContext(NotificationContext)

    const isLoading = useValue<boolean>(false)
    const isCheckingSession = useValue<boolean>(true)
    const isPassword = useValue<boolean>(false)
    const txtLogin = useValue<string>('')
    const txtPassword = useValue<string>('')

    // Хранилище страницы - Авторизация
    const { isAuthorized, actionLogin, actionCheckAuth } = useLogin(useShallow((state) => ({
        isAuthorized: state.isAuthorized,
        actionLogin: state.actionLogin,
        actionCheckAuth: state.actionCheckAuth,
    })))

    useEffect(() => {
        let isCancelled = false
        let retryTimeoutID: ReturnType<typeof setTimeout> | null = null

        const restoreSession = async () => {
            try {
                const response = await actionCheckAuth()
                if (isCancelled) return

                if (response.status === 200 || isAuthenticationRejected(response.status)) {
                    isCheckingSession.setValue(false)
                    return
                }

                retryTimeoutID = setTimeout(restoreSession, SESSION_RESTORE_RETRY_MS)
            } catch (error) {
                reportUnexpectedClientError(error, 'Login.actionCheckAuth')
                if (!isCancelled) retryTimeoutID = setTimeout(restoreSession, SESSION_RESTORE_RETRY_MS)
            }
        }

        void restoreSession()

        return () => {
            isCancelled = true
            if (retryTimeoutID) clearTimeout(retryTimeoutID)
        }
    }, [actionCheckAuth]) // eslint-disable-line react-hooks/exhaustive-deps

    
    /** Нажатие на кнопку войти в форме авторизации */
    const handleButtonClickLogin = async () => {
        if (!txtLogin.value || !txtPassword.value) {
            return notify && notifyOpen('Необходимо ввести имя пользователя и его пароль.', 'info', 2000, notify)
        }
        
        isLoading.setValue(true) // Блокируем кнопку

        /** Выполняем запрос на авторизацию */
        const response = await actionLogin(txtLogin.value, txtPassword.value)
        if (response.status !== 200) notify && notifyOpen(response.message, 'error', 2000, notify)

        isLoading.setValue(false) // Разблокируем кнопку
    }

    /** Обработчик при нажатии клавиши Enter на полях имени пользователя и пароля */
    const handleFormKeyEnterLogin = (event: React.KeyboardEvent<HTMLFormElement>) => {
        if (event.key === 'Enter') handleButtonClickLogin()
    }

    /** Свойства передаваемые в компоненту */
    const propsToComponent: ILoginPage = {
        isLoading,
        isPassword,
        txtLogin,
        txtPassword,
        
        theme,
        toggleTheme,

        handleButtonClickLogin,
        handleFormKeyEnterLogin,
    }
    
    return <>
        <Loader isOpen={isCheckingSession.value} title='ЗАГРУЗКА' />
        {!isCheckingSession.value && (!isAuthorized
            ? <LoginPage {...propsToComponent} /> 
            : <Navigate to="/lk" />)}
    </>
}